import React, { useEffect, useRef, useState } from "react";
import aboutImg from "../assets/img/about.jpg";

interface AboutSectionProps {
  statistics: {
    students: number;
    courses: number;
    teachers: number;
    awards: number;
  };
}

const AboutSection: React.FC<AboutSectionProps> = ({ statistics }) => {
  const [counters, setCounters] = useState({
    students: 0,
    courses: 0,
    teachers: 0,
    awards: 0,
  });

  const animationRef = useRef<number | null>(null);

  useEffect(() => {
    const duration = 1800;
    const startTime = performance.now();

    const animateCounters = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      const easeOut = 1 - Math.pow(1 - progress, 3);

      setCounters({
        students: Math.round(statistics.students * easeOut),
        courses: Math.round(statistics.courses * easeOut),
        teachers: Math.round(statistics.teachers * easeOut),
        awards: Math.round(statistics.awards * easeOut),
      });

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animateCounters);
      }
    };

    animationRef.current = requestAnimationFrame(animateCounters);

    return () => {
      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [statistics]);

  return (
    <>
      <style>
        {`
          /* =========================================================
             ABOUT SECTION
          ========================================================= */

          .growniverse-about-section {
            position: relative;
            display: block;
            width: 100%;
            height: auto;
            min-height: auto;

            margin: 0;
            padding: 80px 20px;

            overflow: hidden;

            background:
              radial-gradient(
                circle at 10% 20%,
                rgba(0, 246, 255, 0.055),
                transparent 30%
              ),
              radial-gradient(
                circle at 90% 80%,
                rgba(139, 92, 255, 0.05),
                transparent 30%
              ),
              linear-gradient(
                135deg,
                #020306 0%,
                #050912 50%,
                #020306 100%
              );

            color: #ffffff;
            border-bottom: 1px solid rgba(0, 246, 255, 0.12);

            box-sizing: border-box;

            isolation: isolate;
          }

          .growniverse-about-section *,
          .growniverse-about-section *::before,
          .growniverse-about-section *::after {
            box-sizing: border-box;
          }


          /* =========================================================
             BACKGROUND
          ========================================================= */

          .about-background-grid {
            position: absolute;
            inset: 0;

            width: 100%;
            height: 100%;

            pointer-events: none;

            opacity: 0.07;

            background-image:
              linear-gradient(
                rgba(255,255,255,0.05) 1px,
                transparent 1px
              ),
              linear-gradient(
                90deg,
                rgba(255,255,255,0.05) 1px,
                transparent 1px
              );

            background-size: 34px 34px;

            z-index: -2;
          }

          .about-glow-cyan {
            position: absolute;

            width: 380px;
            height: 380px;

            left: -220px;
            top: -120px;

            border-radius: 50%;

            background: rgba(0,246,255,0.07);

            filter: blur(100px);

            pointer-events: none;

            z-index: -1;
          }

          .about-glow-purple {
            position: absolute;

            width: 350px;
            height: 350px;

            right: -200px;
            bottom: -150px;

            border-radius: 50%;

            background: rgba(139,92,255,0.07);

            filter: blur(100px);

            pointer-events: none;

            z-index: -1;
          }


          /* =========================================================
             MAIN CONTAINER
          ========================================================= */

          .about-inner {
            position: relative;

            width: 100%;
            max-width: 1180px;

            margin: 0 auto;

            z-index: 2;
          }


          /* =========================================================
             MAIN GRID
          ========================================================= */

          .about-main-grid {
            display: grid;

            grid-template-columns:
              minmax(0, 1fr)
              minmax(0, 1fr);

            gap: 60px;

            align-items: center;

            width: 100%;
          }


          .about-image-column,
          .about-content-column {
            width: 100%;
            min-width: 0;
          }


          /* =========================================================
             IMAGE
          ========================================================= */

          .about-image-wrapper {
            position: relative;

            display: block;

            width: 100%;

            padding: 8px;

            border: 1px solid rgba(0,246,255,0.18);

            background:
              rgba(0,246,255,0.025);

            box-shadow:
              0 20px 60px rgba(0,0,0,0.55),
              0 0 35px rgba(0,246,255,0.04);

            overflow: hidden;
          }

          .about-image {
            display: block;

            width: 100%;
            height: auto;

            max-width: 100%;

            object-fit: cover;

            filter:
              brightness(0.82)
              contrast(1.05);

            box-shadow:
              0 0 30px rgba(0,0,0,0.6);
          }

          .about-image-top-line {
            position: absolute;

            top: -1px;
            left: 25px;

            width: 90px;
            height: 2px;

            background: #00f6ff;

            box-shadow:
              0 0 12px rgba(0,246,255,0.8);

            z-index: 3;
          }

          .about-image-overlay {
            position: absolute;

            inset: 8px;

            background:
              linear-gradient(
                135deg,
                rgba(0,246,255,0.04),
                transparent 45%,
                rgba(139,92,255,0.04)
              );

            pointer-events: none;

            z-index: 2;
          }

          .about-image-bottom-line {
            position: absolute;

            bottom: 7px;

            left: 15%;
            right: 15%;

            height: 1px;

            background:
              linear-gradient(
                90deg,
                transparent,
                rgba(0,246,255,0.45),
                transparent
              );

            z-index: 3;
          }


          /* =========================================================
             LABEL
          ========================================================= */

          .about-label {
            display: inline-flex;

            align-items: center;

            gap: 8px;

            margin-bottom: 14px;

            padding: 7px 12px;

            border: 1px solid rgba(0,246,255,0.18);

            border-radius: 4px;

            background:
              rgba(0,246,255,0.025);

            color: #00f6ff;

            font-size: 9px;

            font-weight: 700;

            letter-spacing: 2px;

            text-transform: uppercase;
          }

          .about-label-dot {
            width: 6px;
            height: 6px;

            flex: 0 0 6px;

            border-radius: 50%;

            background: #00f6ff;

            box-shadow:
              0 0 10px #00f6ff;
          }


          /* =========================================================
             TITLE
          ========================================================= */

          .about-title {
            margin: 0 0 18px;

            color: #ffffff;

            font-size: clamp(
              30px,
              4vw,
              44px
            );

            font-weight: 800;

            line-height: 1.15;

            letter-spacing: -1px;

            text-shadow:
              0 0 25px rgba(255,255,255,0.08);
          }

          .about-title-line {
            width: 70px;
            height: 2px;

            margin-bottom: 22px;

            background:
              linear-gradient(
                90deg,
                #00f6ff,
                transparent
              );

            box-shadow:
              0 0 10px rgba(0,246,255,0.6);
          }


          /* =========================================================
             DESCRIPTION
          ========================================================= */

          .about-description {
            max-width: 650px;

            margin: 0 0 35px;

            color: #aeb8c6;

            font-size: 15px;

            line-height: 1.9;
          }


          /* =========================================================
             STATISTICS
          ========================================================= */

          .about-statistics {
            display: grid;

            grid-template-columns:
              repeat(2, minmax(0, 1fr));

            gap: 12px;

            width: 100%;
          }

          .about-stat-card {
            width: 100%;
            min-width: 0;

            padding: 18px 15px;

            border:
              1px solid rgba(255,255,255,0.08);

            border-radius: 8px;

            background:
              rgba(255,255,255,0.018);

            box-shadow:
              inset 0 0 20px rgba(255,255,255,0.008);

            transition:
              transform 0.25s ease,
              border-color 0.25s ease,
              background 0.25s ease;
          }

          .about-stat-card:hover {
            border-color:
              rgba(0,246,255,0.25);

            background:
              rgba(0,246,255,0.025);

            transform:
              translateY(-2px);
          }

          .about-stat-number {
            margin-bottom: 8px;

            font-size: 28px;

            font-weight: 800;

            line-height: 1;
          }

          .about-stat-number.cyan {
            color: #00f6ff;

            text-shadow:
              0 0 15px rgba(0,246,255,0.35);
          }

          .about-stat-number.purple {
            color: #8b7cff;

            text-shadow:
              0 0 15px rgba(139,92,255,0.35);
          }

          .about-stat-label {
            color: #8d9aaa;

            font-size: 11px;

            font-weight: 700;

            letter-spacing: 1.2px;

            text-transform: uppercase;
          }


          /* =========================================================
             BOTTOM LINE
          ========================================================= */

          .about-bottom-line {
            position: absolute;

            left: 18%;
            right: 18%;
            bottom: 0;

            height: 1px;

            background:
              linear-gradient(
                90deg,
                transparent,
                rgba(0,246,255,0.35),
                transparent
              );

            pointer-events: none;
          }


          /* =========================================================
             TABLET
          ========================================================= */

          @media (max-width: 991px) {

            .growniverse-about-section {
              padding: 65px 24px;
            }

            .about-main-grid {
              grid-template-columns: 1fr;

              gap: 45px;
            }

            .about-image-column,
            .about-content-column {
              width: 100%;

              max-width: 720px;

              margin-left: auto;
              margin-right: auto;
            }

          }


          /* =========================================================
             MOBILE
          ========================================================= */

          @media (max-width: 767px) {

            .growniverse-about-section {
              display: block;

              width: 100%;

              height: auto;
              min-height: 0;

              padding: 45px 16px;

              overflow: hidden;
            }

            .about-inner {
              width: 100%;

              max-width: 100%;
            }

            .about-main-grid {
              display: flex;

              flex-direction: column;

              gap: 35px;

              width: 100%;
            }

            .about-image-column,
            .about-content-column {
              width: 100%;

              max-width: 100%;

              min-width: 0;
            }


            /* IMAGE */

            .about-image-wrapper {
              width: 100%;

              padding: 6px;
            }

            .about-image {
              width: 100%;

              height: auto;

              min-height: 0;
            }

            .about-image-overlay {
              inset: 6px;
            }


            /* CONTENT */

            .about-label {
              margin-bottom: 12px;
            }

            .about-title {
              font-size: 32px;

              margin-bottom: 15px;
            }

            .about-title-line {
              margin-bottom: 18px;
            }

            .about-description {
              width: 100%;

              max-width: 100%;

              font-size: 14px;

              line-height: 1.75;

              margin-bottom: 25px;
            }


            /* STATISTICS */

            .about-statistics {
              width: 100%;

              grid-template-columns:
                repeat(2, minmax(0, 1fr));

              gap: 10px;
            }

            .about-stat-card {
              width: 100%;

              padding: 16px 12px;
            }

            .about-stat-number {
              font-size: 24px;
            }

            .about-stat-label {
              font-size: 9px;

              letter-spacing: 0.8px;
            }


            /* GLOWS */

            .about-glow-cyan {
              width: 200px;
              height: 200px;

              left: -130px;
              top: -50px;

              filter: blur(70px);
            }

            .about-glow-purple {
              width: 180px;
              height: 180px;

              right: -120px;
              bottom: -80px;

              filter: blur(70px);
            }

            .about-bottom-line {
              left: 10%;
              right: 10%;
            }
          }


          /* =========================================================
             SMALL MOBILE
          ========================================================= */

          @media (max-width: 400px) {

            .growniverse-about-section {
              padding: 40px 12px;
            }

            .about-main-grid {
              gap: 30px;
            }

            .about-title {
              font-size: 29px;
            }

            .about-description {
              font-size: 13px;

              line-height: 1.7;
            }

            .about-stat-card {
              padding: 14px 10px;
            }

            .about-stat-number {
              font-size: 22px;
            }

            .about-stat-label {
              font-size: 8px;
            }

          }


          /* =========================================================
             IMPORTANT GLOBAL MOBILE SAFETY
          ========================================================= */

          @media (max-width: 767px) {

            html,
            body,
            #root {
              width: 100%;
              max-width: 100%;
            }

            .growniverse-about-section {
              flex-shrink: 0;
            }

          }
        `}
      </style>

      <section className="growniverse-about-section">

        {/* Background */}
        <div
          className="about-background-grid"
          aria-hidden="true"
        />

        <div
          className="about-glow-cyan"
          aria-hidden="true"
        />

        <div
          className="about-glow-purple"
          aria-hidden="true"
        />

        <div className="about-inner">

          <div className="about-main-grid">

            {/* =====================================================
                IMAGE
            ====================================================== */}

            <div className="about-image-column">

              <div className="about-image-wrapper">

                <div className="about-image-top-line" />

                <img
                  className="about-image"
                  src={aboutImg}
                  alt="About Growniverse"
                />

                <div className="about-image-overlay" />

                <div className="about-image-bottom-line" />

              </div>

            </div>


            {/* =====================================================
                CONTENT
            ====================================================== */}

            <div className="about-content-column">

              <div className="about-label">
                <span className="about-label-dot" />
                GROWNIVERSE
              </div>

              <h2 className="about-title">
                About Us
              </h2>

              <div className="about-title-line" />

              <p className="about-description">
                Growniverse is your Digital Learning Platform
                where you can learn new knowledge and skills
                without any physical distance barrier. You not
                only get classes, but you will also get live
                webinars and specially Real Life Experience of
                Entrepreneurship.
              </p>


              {/* =================================================
                  STATISTICS
              ================================================== */}

              <div className="about-statistics">

                <div className="about-stat-card">

                  <div className="about-stat-number cyan">
                    {counters.students}
                  </div>

                  <div className="about-stat-label">
                    Happy Students
                  </div>

                </div>


                <div className="about-stat-card">

                  <div className="about-stat-number purple">
                    {counters.courses}+
                  </div>

                  <div className="about-stat-label">
                    Our Courses
                  </div>

                </div>


                <div className="about-stat-card">

                  <div className="about-stat-number cyan">
                    {counters.teachers}
                  </div>

                  <div className="about-stat-label">
                    Our Teachers
                  </div>

                </div>


                <div className="about-stat-card">

                  <div className="about-stat-number purple">
                    {counters.awards}
                  </div>

                  <div className="about-stat-label">
                    Awards Won
                  </div>

                </div>

              </div>

            </div>

          </div>

        </div>

        <div
          className="about-bottom-line"
          aria-hidden="true"
        />

      </section>
    </>
  );
};

export default AboutSection;